import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { RefreshCw, LogOut, Wifi, WifiOff, RotateCcw, GripVertical, ChevronDown, ChevronUp, Users, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Clock from '@/components/Clock';
import CountdownTimer from '@/components/CountdownTimer';
import WeatherTicker from '@/components/WeatherTicker';
import NewsSection from '@/components/NewsSection';
import EventsSection from '@/components/EventsSection';
import TidesSection from '@/components/TidesSection';
import TrafficSection from '@/components/TrafficSection';
import TrainInfoSection from '@/components/TrainInfoSection';
import BusInfoSection from '@/components/BusInfoSection';
import OnThisDaySection from '@/components/OnThisDaySection';
import MusicNewsSection from '@/components/MusicNewsSection';
import WeatherForecastSection from '@/components/WeatherForecastSection';
import NationalNewsSection from '@/components/NationalNewsSection';
import SportsNewsSection from '@/components/SportsNewsSection';
import StationInfoSection from '@/components/StationInfoSection';
import SunTimesSection from '@/components/SunTimesSection';
import BankHolidaysSection from '@/components/BankHolidaysSection';
import PetrolPricesSection from '@/components/PetrolPricesSection';
import AirQualitySection from '@/components/AirQualitySection';
import SchoolClosuresSection from '@/components/SchoolClosuresSection';
import JokeOfTheHourSection from '@/components/JokeOfTheHourSection';
import MajesticWhatsOnSection from '@/components/MajesticWhatsOnSection';
import PrincessWhatsOnSection from '@/components/PrincessWhatsOnSection';
import CornExchangeWhatsOnSection from '@/components/CornExchangeWhatsOnSection';
import PresenterUpdatesSection from '@/components/PresenterUpdatesSection';
import EmailReaderSection from '@/components/EmailReaderSection';
import CompetitionSection from '@/components/CompetitionSection';
import RoadworksSection from '@/components/RoadworksSection';
import TalkIdeasSection from '@/components/TalkIdeasSection';
import CallerNotesSection from '@/components/CallerNotesSection';
import BreakingNewsSection from '@/components/BreakingNewsSection';
import MicLiveIndicator from '@/components/MicLiveIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REFRESH_INTERVAL = 60 * 60 * 1000;

// Default section order
const DEFAULT_SECTION_ORDER = [
  'presenter-updates',
  'breaking-news',
  'caller-notes',
  'talk-ideas',
  'studio-email',
  'competition',
  'lynn-news',
  'national-news',
  'traffic',
  'roadworks',
  'sports-news',
  'celebrity-news',
  'music-news',
  'funny-news',
  'local-events',
  'weather-forecast',
  'tides',
  'majestic-whatson',
  'princess-whatson',
  'cornexchange-whatson',
  'joke-of-the-hour',
  'station-info',
  'school-closures',
  'sun-times',
  'bank-holidays',
  'petrol-prices',
  'air-quality',
  'trains',
  'buses',
  'on-this-day'
];

// Sortable Section Wrapper Component
const SortableSection = ({ id, children, isCollapsed, onToggleCollapse, title }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} className={`sortable-section ${isCollapsed ? 'collapsed' : ''}`} data-testid={`sortable-${id}`}>
      <div className="section-controls">
        <div className="drag-handle" {...attributes} {...listeners}>
          <GripVertical size={20} className="drag-icon" />
        </div>
        <button 
          className="collapse-btn" 
          onClick={() => onToggleCollapse(id)}
          title={isCollapsed ? "Expand section" : "Minimize section"}
        >
          {isCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
        </button>
      </div>
      {isCollapsed ? (
        <div className="collapsed-title">{title}</div>
      ) : (
        children
      )}
    </div>
  );
};

// Admin Panel Modal
const AdminPanel = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
        setUsers(data);
      } catch {
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  return (
    <div className="admin-modal-overlay" onClick={onClose} data-testid="admin-panel">
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h2><Users size={22} /> Registered Users</h2>
          <button className="admin-close-btn" onClick={onClose} data-testid="close-admin-panel">&times;</button>
        </div>
        <div className="admin-modal-body">
          {loading ? (
            <div className="loading-placeholder">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="empty-state">No users found</div>
          ) : (
            <table className="admin-users-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Registered</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id}>
                    <td>{u.username}</td>
                    <td>{u.display_name}</td>
                    <td className={`role-badge ${u.role}`}>{u.role}</td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const DashboardPage = ({ onLogout, user }) => {
  const [lynnNews, setLynnNews] = useState([]);
  const [celebrityNews, setCelebrityNews] = useState([]);
  const [funnyNews, setFunnyNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [tides, setTides] = useState([]);
  const [traffic, setTraffic] = useState([]);
  const [weather, setWeather] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const [activeId, setActiveId] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState([]);
  const [showAdmin, setShowAdmin] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Load saved layout from API on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const { data } = await axios.get(`${API}/user/preferences`, { withCredentials: true });
        if (data.layout && Array.isArray(data.layout)) {
          // Merge: use saved order but ensure new sections are included
          const saved = data.layout;
          const missing = DEFAULT_SECTION_ORDER.filter(s => !saved.includes(s));
          setSectionOrder([...saved, ...missing]);
        }
        if (data.collapsed && Array.isArray(data.collapsed)) {
          setCollapsedSections(data.collapsed);
        }
      } catch (err) {
        console.warn('Could not load saved layout, using defaults:', err);
      } finally {
        setPrefsLoaded(true);
      }
    };
    loadPreferences();
  }, []);

  // Save preferences to API
  const savePreferences = useCallback(async (layout, collapsed) => {
    try {
      await axios.post(`${API}/user/preferences`, { layout, collapsed }, { withCredentials: true });
    } catch (err) {
      console.error('Failed to save layout preferences:', err);
      toast.error('Could not save your layout');
    }
  }, []);

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchAllData = useCallback(async () => {
    try {
      const [lynnRes, celebRes, funnyRes, eventsRes, tidesRes, trafficRes, weatherRes] = await Promise.allSettled([
        axios.get(`${API}/news/lynn`, { withCredentials: true }),
        axios.get(`${API}/news/celebrity`, { withCredentials: true }),
        axios.get(`${API}/news/funny`, { withCredentials: true }),
        axios.get(`${API}/events/local`, { withCredentials: true }),
        axios.get(`${API}/tides`, { withCredentials: true }),
        axios.get(`${API}/traffic`, { withCredentials: true }),
        axios.get(`${API}/weather`, { withCredentials: true })
      ]);

      if (lynnRes.status === 'fulfilled') setLynnNews(lynnRes.value.data);
      if (celebRes.status === 'fulfilled') setCelebrityNews(celebRes.value.data);
      if (funnyRes.status === 'fulfilled') setFunnyNews(funnyRes.value.data);
      if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value.data);
      if (tidesRes.status === 'fulfilled') setTides(tidesRes.value.data);
      if (trafficRes.status === 'fulfilled') setTraffic(trafficRes.value.data);
      if (weatherRes.status === 'fulfilled') setWeather(weatherRes.value.data);

      setIsOnline(true);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsOnline(false);
      toast.error('Failed to refresh data');
    }
  }, []);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  const handleManualRefresh = () => {
    toast.info('Refreshing content...');
    fetchAllData();
  };

  // Handle drag end
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setSectionOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        savePreferences(newOrder, collapsedSections);
        toast.success('Layout saved');
        
        return newOrder;
      });
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Reset layout to default
  const resetLayout = () => {
    setSectionOrder(DEFAULT_SECTION_ORDER);
    setCollapsedSections([]);
    savePreferences(DEFAULT_SECTION_ORDER, []);
    toast.success('Layout reset to default');
  };

  // Toggle section collapse
  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => {
      const newCollapsed = prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId];
      
      savePreferences(sectionOrder, newCollapsed);
      return newCollapsed;
    });
  };

  // Get section title for collapsed view
  const getSectionTitle = (sectionId) => {
    const titles = {
      'presenter-updates': 'Presenter Updates & Promos',
      'breaking-news': 'Breaking News',
      'caller-notes': 'Caller Notes',
      'talk-ideas': 'AI Talk Ideas',
      'studio-email': 'Studio Email',
      'competition': 'Latest Competition',
      'lynn-news': 'Lynn News',
      'national-news': 'UK National News',
      'traffic': 'Traffic Updates',
      'roadworks': 'Road Works & Closures',
      'sports-news': 'Sports News',
      'celebrity-news': 'UK Celebrity News',
      'music-news': 'UK Music & Chart News',
      'funny-news': 'Odd & Funny Stories',
      'local-events': 'Local Events',
      'weather-forecast': 'Weather Forecast',
      'tides': 'Hunstanton Tides',
      'majestic-whatson': 'Majestic Cinema',
      'princess-whatson': 'Princess Theatre',
      'cornexchange-whatson': "King's Lynn Corn Exchange",
      'joke-of-the-hour': 'Joke of the Hour',
      'station-info': 'Station Information',
      'school-closures': 'Norfolk School Closures',
      'sun-times': "Today's Sun Times",
      'bank-holidays': 'UK Bank Holidays',
      'petrol-prices': 'Fuel Prices',
      'air-quality': 'Air Quality',
      'trains': 'Live Train Data',
      'buses': 'Live Bus Tracker',
      'on-this-day': 'On This Day'
    };
    return titles[sectionId] || 'Section';
  };

  // Map section IDs to their components
  const getSectionComponent = (sectionId) => {
    switch (sectionId) {
      case 'presenter-updates':
        return <PresenterUpdatesSection />;
      case 'breaking-news':
        return <BreakingNewsSection />;
      case 'caller-notes':
        return <CallerNotesSection />;
      case 'talk-ideas':
        return <TalkIdeasSection />;
      case 'studio-email':
        return <EmailReaderSection />;
      case 'competition':
        return <CompetitionSection />;
      case 'lynn-news':
        return (
          <NewsSection 
            title="Lynn News" 
            news={lynnNews} 
            icon="📰"
            testId="lynn-news-section"
          />
        );
      case 'national-news':
        return <NationalNewsSection />;
      case 'traffic':
        return <TrafficSection traffic={traffic} />;
      case 'roadworks':
        return <RoadworksSection />;
      case 'sports-news':
        return <SportsNewsSection />;
      case 'celebrity-news':
        return (
          <NewsSection 
            title="UK Celebrity News" 
            news={celebrityNews} 
            icon="⭐"
            testId="celebrity-news-section"
          />
        );
      case 'music-news':
        return <MusicNewsSection />;
      case 'funny-news':
        return (
          <NewsSection 
            title="Odd & Funny Stories" 
            news={funnyNews} 
            icon="😄"
            testId="funny-news-section"
          />
        );
      case 'local-events':
        return <EventsSection events={events} />;
      case 'weather-forecast':
        return <WeatherForecastSection />;
      case 'tides':
        return <TidesSection tides={tides} />;
      case 'majestic-whatson':
        return <MajesticWhatsOnSection />;
      case 'princess-whatson':
        return <PrincessWhatsOnSection />;
      case 'cornexchange-whatson':
        return <CornExchangeWhatsOnSection />;
      case 'joke-of-the-hour':
        return <JokeOfTheHourSection />;
      case 'station-info':
        return <StationInfoSection />;
      case 'school-closures':
        return <SchoolClosuresSection />;
      case 'sun-times':
        return <SunTimesSection />;
      case 'bank-holidays':
        return <BankHolidaysSection />;
      case 'petrol-prices':
        return <PetrolPricesSection />;
      case 'air-quality':
        return <AirQualitySection />;
      case 'trains':
        return <TrainInfoSection />;
      case 'buses':
        return <BusInfoSection />;
      case 'on-this-day':
        return <OnThisDaySection />;
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container" data-testid="dashboard-container">
      {/* Mic Live Banner */}
      <MicLiveIndicator />

      {/* Header Bar */}
      <div className="dashboard-header">
        <div className="header-left">
          <img 
            src="https://customer-assets.emergentagent.com/job_presenter-toolbox/artifacts/xguxj4wx_KL1%20Logo-01.png" 
            alt="KL1 Radio Logo" 
            className="dashboard-logo"
          />
          <div>
            <h1 className="header-title">Presenter Portal</h1>
            <p className="header-subtitle">
              Signed in as <strong>{user?.display_name || user?.username}</strong>
              {user?.role === 'admin' && <span className="admin-badge">Admin</span>}
            </p>
          </div>
        </div>
        
        <div className="header-center">
          <Clock />
          <CountdownTimer />
        </div>
        
        <div className="header-right">
          <div className="status-indicator">
            {isOnline ? <Wifi size={20} /> : <WifiOff size={20} />}
            <span>{isOnline ? 'Online' : 'Offline'}</span>
          </div>
          {user?.role === 'admin' && (
            <Button 
              onClick={() => setShowAdmin(true)} 
              variant="outline" 
              size="sm"
              className="admin-btn"
              title="Manage Users"
              data-testid="admin-users-button"
            >
              <Users size={16} />
            </Button>
          )}
          <Button 
            onClick={resetLayout} 
            variant="outline" 
            size="sm"
            className="reset-layout-btn"
            title="Reset Layout"
            data-testid="reset-layout-button"
          >
            <RotateCcw size={16} />
          </Button>
          <Button 
            onClick={handleManualRefresh} 
            variant="outline" 
            size="sm"
            className="refresh-btn"
            data-testid="refresh-button"
          >
            <RefreshCw size={16} />
          </Button>
          <Button 
            onClick={onLogout} 
            variant="destructive" 
            size="sm"
            data-testid="logout-button"
          >
            <LogOut size={16} />
          </Button>
        </div>
      </div>

      {/* Weather Ticker */}
      <WeatherTicker weather={weather} />

      {/* Main Content Grid with Drag and Drop */}
      <div className="dashboard-content">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={sectionOrder} strategy={rectSortingStrategy}>
            <div className="content-grid">
              {sectionOrder.map((sectionId) => (
                <SortableSection 
                  key={sectionId} 
                  id={sectionId}
                  isCollapsed={collapsedSections.includes(sectionId)}
                  onToggleCollapse={toggleSectionCollapse}
                  title={getSectionTitle(sectionId)}
                >
                  {getSectionComponent(sectionId)}
                </SortableSection>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Footer */}
      <div className="dashboard-footer">
        <p>Last updated: {lastRefresh.toLocaleTimeString()} | Auto-refresh: Every hour</p>
      </div>

      {/* Admin Panel */}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
    </div>
  );
};

export default DashboardPage;
